import { useState } from "react";
import { toast } from "sonner";
import { Ambulance, PhoneCall, Check, PenLine, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmt } from "@/lib/clinical";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function StepRow({ label, at, by, extra, action }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`h-6 w-6 rounded-full grid place-items-center shrink-0 mt-0.5 ${at ? "bg-[#007F3B] text-white" : "bg-slate-200 text-slate-400"}`}>
        <Check className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${at ? "text-[#0F2547]" : "text-slate-400"}`}>{label}</p>
        {at && <p className="text-xs text-slate-500">{fmt(at)}{by ? ` · ${by}` : ""}</p>}
        {extra}
        {action}
      </div>
    </div>
  );
}

export default function TransportSection({ patient, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const t = patient.transport;
  const [cad, setCad] = useState("");
  const [showCad, setShowCad] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [receiver, setReceiver] = useState("");
  const [role, setRole] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!t && patient.status === "discharged") return null;

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const start = (method) =>
    run(() => api.post(`/patients/${patient.id}/transport`, { method, cad_number: cad }),
      method === "999" ? "999 call logged" : "WEMS transport allocated");

  const step = (s) =>
    run(() => api.post(`/patients/${patient.id}/transport/step`, { step: s }), "Journey updated");

  const remove = () => {
    if (!window.confirm("Delete this transport record? If the patient was discharged as taken to hospital, they'll be returned to active care.")) return;
    run(() => api.delete(`/patients/${patient.id}/transport`), "Transport record deleted");
  };

  const confirmHandover = () =>
    run(async () => {
      await api.post(`/patients/${patient.id}/transport/handover`, {
        receiver_name: receiver, receiver_role: role, signature_data: sig || "",
      });
      setHandoverOpen(false);
      setReceiver(""); setRole(""); setSig(null);
    }, "Patient handed over and signed off");

  const actionBtn = (onClick, label, testid, disabled = false) => (
    <button onClick={onClick} disabled={busy || disabled} data-testid={testid}
      className="mt-1.5 h-9 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-xs font-bold">
      {label}
    </button>
  );

  return (
    <div className="bg-white rounded-xl border-2 border-[#ED8B00]/60 p-5" data-testid="transport-section">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
        <Ambulance className="h-4 w-4 text-[#ED8B00]" /> Hospital transport
        {t && isAdmin && (
          <button onClick={remove} disabled={busy} data-testid="transport-delete"
            className="ml-auto h-8 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 disabled:opacity-50 transition-colors text-xs font-bold inline-flex items-center gap-1.5">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        )}
      </p>

      {!t && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button onClick={() => start("wems")} disabled={busy} data-testid="transport-start-wems"
            className="h-12 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] disabled:opacity-50 transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
            <Ambulance className="h-4 w-4" /> WEMS vehicle transport
          </button>
          {!showCad ? (
            <button onClick={() => setShowCad(true)} data-testid="transport-show-999"
              className="h-12 rounded-lg bg-[#D5281B] hover:bg-[#b02218] transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
              <PhoneCall className="h-4 w-4" /> 999 called — log CAD
            </button>
          ) : (
            <div className="flex gap-2">
              <input value={cad} onChange={(e) => setCad(e.target.value)} placeholder="CAD number" data-testid="transport-cad-input"
                className="flex-1 h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D5281B]" />
              <button onClick={() => start("999")} disabled={busy || !cad.trim()} data-testid="transport-start-999"
                className="h-12 px-4 rounded-lg bg-[#D5281B] hover:bg-[#b02218] disabled:opacity-50 transition-colors text-white text-xs font-bold">
                Log call
              </button>
            </div>
          )}
        </div>
      )}

      {t?.method === "999" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm font-bold text-[#D5281B]" data-testid="transport-cad-label">999 CAD: {t.cad_number}</p>
            <p className="text-xs text-slate-600 mt-0.5">Call made {fmt(t.call_made_at)} · by {t.call_made_by}</p>
          </div>
          <StepRow label="Handed over to ambulance crew / hospital" at={t.handover_signed_at} by={t.handover_receiver}
            action={!t.handover_signed_at && actionBtn(() => { setReceiver(""); setHandoverOpen(true); }, "Mark handed over", "transport-handover-open")} />
        </div>
      )}

      {t?.method === "wems" && (
        <div className="space-y-4">
          <StepRow label="WEMS vehicle allocated" at={t.allocated_at} by={t.allocated_by} />
          <StepRow label="Left site (en route to hospital)" at={t.left_at} by={t.left_by}
            action={!t.left_at && actionBtn(() => step("left"), "Confirm left site", "transport-step-left")} />
          <StepRow label="Arrived at hospital" at={t.arrived_hospital_at} by={t.arrived_by}
            action={t.left_at && !t.arrived_hospital_at && actionBtn(() => step("arrived"), "Confirm arrived at hospital", "transport-step-arrived")} />
          <StepRow label="Handover signed at hospital" at={t.handover_signed_at}
            by={t.handover_signed_at ? `${t.handover_receiver}${t.handover_role ? ` (${t.handover_role})` : ""}` : ""}
            extra={t.handover_signature && (
              <img src={t.handover_signature} alt="Handover signature" data-testid="transport-handover-signature"
                className="h-14 mt-1 bg-white border border-slate-200 rounded" />
            )}
            action={t.arrived_hospital_at && !t.handover_signed_at && actionBtn(() => setHandoverOpen(true), "Open handover signature", "transport-handover-open")} />
          <StepRow label="Back on site — vehicle available" at={t.back_on_site_at} by={t.back_by}
            action={t.handover_signed_at && !t.back_on_site_at && actionBtn(() => step("back"), "Confirm back on site", "transport-step-back")} />
          {t.back_on_site_at && (
            <p className="text-xs font-bold text-[#007F3B] bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="transport-complete-label">
              Vehicle back on site and available for tasking.
            </p>
          )}
        </div>
      )}

      <Dialog open={handoverOpen} onOpenChange={setHandoverOpen}>
        <DialogContent className="max-w-md" data-testid="transport-handover-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
              <PenLine className="h-5 w-5 text-[#005EB8]" /> Patient handover sign-off
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              The receiving clinician confirms handover. Signing stamps the date and time, discharges the patient as taken to hospital, and notifies the event chat.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Receiving clinician's name *</label>
              <input value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="e.g. Nurse Jane Davies" data-testid="handover-receiver-input"
                className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Role / department</label>
              <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Triage nurse, A&E" data-testid="handover-role-input"
                className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            </div>
            {t?.method === "wems" && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Receiving signature *</label>
                <SignaturePad key={String(handoverOpen)} onSign={setSig} />
              </div>
            )}
            <button onClick={confirmHandover} disabled={busy || !receiver.trim() || (t?.method === "wems" && !sig)}
              data-testid="handover-confirm-button"
              className="w-full h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 transition-colors text-white text-sm font-bold">
              {busy ? "Saving…" : "Sign & complete handover"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
